import React, { useEffect, useState } from 'react';
import { XIcon, HashIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Customer } from './CustomerCard';
interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (customer: Customer) => void;
  customer: Customer | null;
}
export function EditCustomerModal({
  isOpen,
  onClose,
  onSave,
  customer
}: EditCustomerModalProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [secondaryContactName, setSecondaryContactName] = useState('');
  const [secondaryContactPhone, setSecondaryContactPhone] = useState('');
  useEffect(() => {
    if (customer) {
      setName(customer.name);
      setAddress(customer.address || '');
      setPhone(customer.phone || '');
      setEmail(customer.email || '');
      setNotes(customer.notes);
      setSecondaryContactName(customer.secondaryContactName || '');
      setSecondaryContactPhone(customer.secondaryContactPhone || '');
    }
  }, [customer]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer || !name.trim()) return;
    onSave({
      ...customer,
      name,
      address,
      phone,
      email: email || undefined,
      notes,
      secondaryContactName: secondaryContactName || undefined,
      secondaryContactPhone: secondaryContactPhone || undefined
    });
    onClose();
  };
  if (!customer) return null;
  return <AnimatePresence data-id="element-540">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-541" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-542">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8" data-id="element-543">
              <div className="flex items-center justify-between mb-6" data-id="element-544">
                <div data-id="element-545">
                  <h2 className="text-2xl font-semibold text-stone-900" data-id="element-546">
                    Rediger kunde
                  </h2>
                  <div className="flex items-center gap-1.5 mt-1" data-id="element-547">
                    <HashIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-548" />
                    <span className="text-sm font-medium text-teal-600" data-id="element-549">
                      {customer.customerId}
                    </span>
                  </div>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-550">
                  <XIcon className="w-6 h-6" data-id="element-551" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-552">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-553">
                  <Input label="Kundenavn" value={name} onChange={e => setName(e.target.value)} placeholder="Indtast kundenavn" required data-id="element-554" />

                  <Input label="Telefon" value={phone} onChange={e => setPhone(e.target.value)} placeholder="f.eks. +45 12 34 56 78" type="tel" data-id="element-555" />
                </div>

                <Input label="Adresse" value={address} onChange={e => setAddress(e.target.value)} placeholder="Indtast adresse" data-id="element-556" />

                <Input label="Email (valgfri)" value={email} onChange={e => setEmail(e.target.value)} placeholder="kunde@email.dk" type="email" data-id="element-557" />

                <div className="pt-4 border-t border-stone-100" data-id="element-558">
                  <h3 className="text-sm font-medium text-stone-900 mb-3" data-id="element-559">
                    Ekstra kontaktperson (valgfri)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-560">
                    <Input label="Navn" value={secondaryContactName} onChange={e => setSecondaryContactName(e.target.value)} placeholder="Navn på kontaktperson" data-id="element-561" />
                    <Input label="Telefon" value={secondaryContactPhone} onChange={e => setSecondaryContactPhone(e.target.value)} placeholder="Telefonnummer" type="tel" data-id="element-562" />
                  </div>
                </div>

                <div data-id="element-563">
                  <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-564">
                    Noter
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tilføj noter om denne kunde" rows={3} className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-565" />
                </div>

                <div className="flex gap-3 pt-2" data-id="element-566">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-567">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-568">
                    Gem ændringer
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}